<template>
    <div class="relative w-full">
        <textarea
            ref="redactorRef"
            :name="name"
            :placeholder="placeholder"
            :value="value"
        />
        <div
            v-if="hasButtons"
            class="flex save-buttons"
        >
            <el-tooltip
                class="item"
                effect="dark"
                :content="$t('common.revert.changes')"
                placement="top"
            >
                <XIcon
                    class="w-6 h-6 text-red cursor-pointer mx-2"
                    @click="onRevert"
                />
            </el-tooltip>
            <el-tooltip
                class="item"
                effect="dark"
                :content="$t('common.save.changes')"
                placement="top"
            >
                <CheckIcon
                    class="w-6 h-6 text-green cursor-pointer"
                    @click="onUpdate"
                />
            </el-tooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { Tooltip as ElTooltip } from 'element-ui'
import { CheckIcon, XIcon } from 'vue-feather-icons'
import { isRTL } from '@/composables/useDirection'
import i18n from '@/i18n'
import type { RedactorApp } from '@/types/redactor'
import '@/assets/vendor/redactor/_scss/redactor.scss'
import '@/assets/vendor/redactor/_plugins/variable/variable.css'

const props = withDefaults(
    defineProps<{
        hasButtons?: boolean
        value?: string
        placeholder?: string
        name?: string
        editMode?: boolean
        config?: Record<string, unknown>
        availableVariables?: { text: string; value?: string }[]
        buttonsHide?: string[]
        minHeight?: string
    }>(),
    {
        hasButtons: true,
        value: '',
        editMode: true,
        config: () => ({
            plugins: ['alignment', 'fontcolor', 'imagemanager', 'textdirection', 'fontsize', 'customfontsize'],
            imageResizable: true,
            imagePosition: true,
            minHeight: '100px',
            focus: true
        }),
        availableVariables: () => [],
        buttonsHide: () => [],
        minHeight: '30px'
    }
)

const emit = defineEmits<{
    (e: 'change', value: string): void
    (e: 'on-update', value: string): void
}>()

const redactorRef = ref<HTMLTextAreaElement | null>(null)
const redactor = ref<RedactorApp | null>(null)
const currentContent = ref(props.value)

function fileToBase64 (file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result))
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

function handleInput (val: string) {
    currentContent.value = val
    emit('change', val)
}

function init () {
    if (!redactorRef.value) return
    try {
        const callbacks = {
            changed: (html: string) => {
                handleInput(html)
                return html
            },
            image: {
                uploadError: (response: { message?: string }) => {
                    console.warn(response?.message)
                }
            }
        }
        const target = document.querySelector('#block-settings-sidebar .sidebar__content')
        const configWithCallbacks = {
            ...props.config,
            callbacks
        }
        const finalConfig: Record<string, unknown> = {
            ...configWithCallbacks,
            ...(props.buttonsHide?.length ? { buttonsHide: props.buttonsHide } : {}),
            ...(target ? { toolbarFixedTarget: target } : {}),
            lang: i18n.locale,
            direction: isRTL.value ? 'rtl' : 'ltr',
            minHeight: props.minHeight,
            imageUpload: async (
                _data: unknown,
                files: File[] | null,
                _e: unknown,
                upload: { complete: (opts: Record<string, unknown>) => void }
            ) => {
                if (!files?.length) return
                try {
                    const file = files[0]
                    const imgSrc = await fileToBase64(file)
                    upload.complete({
                        file: { url: imgSrc, id: imgSrc }
                    })
                } catch {
                    upload.complete({
                        error: true,
                        message: String(i18n.t('html.editor.image.upload.error'))
                    })
                }
            },
            variables: props.availableVariables.map((v) => v.text),
            availableVariables: props.availableVariables
        }
        const app = window.$R(redactorRef.value, finalConfig)
        redactor.value = app
    } catch (e) {
        console.warn('Redactor init error', e)
    }
}

function onRevert () {
    emit('on-update', props.value)
}

function onUpdate () {
    const content = currentContent.value || props.value
    emit('on-update', content)
}

watch(
    () => props.value,
    (newValue) => {
        if (!redactor.value) {
            return
        }
        const editor = redactor.value.editor
        if (editor.isFocus() || editor.isSourceMode()) {
            return
        }
        redactor.value.source.setCode(newValue || '')
    }
)

watch(isRTL, () => {
    if (!redactor.value) {
        return
    }
    window.$R(redactorRef.value!, 'destroy')
    init()
})

watch(
    () => props.availableVariables,
    () => {
        if (!redactor.value) {
            return
        }
        window.$R(redactorRef.value!, 'destroy')
        init()
    },
    { deep: true }
)

onMounted(async () => {
    await import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/redactor')
    await Promise.all([
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_langs/en.js'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_langs/he.js'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/imagemanager/imagemanager'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/definedlinks/definedlinks'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/alignment/alignment'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/fontsize/fontsize'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/fontcolor/fontcolor'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/textdirection/textdirection'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/customfontsize/customfontsize'),
        import(/* webpackChunkName: "redactor" */ '@/assets/vendor/redactor/_plugins/variable/variable'),
    ])
    init()
})

defineExpose({
    redactor,
    init,
    onRevert,
    onUpdate
})
</script>

<style scoped>
.save-buttons {
    @apply absolute;
    right: 10px;
    top: 15px;
    z-index: 100;
}

.rtl .save-buttons {
    left: 10px;
    right: inherit;
}
</style>
